import { useMonitoringClient } from '@xometry/ui';
import { AiWriterModal } from 'components/AiWriterModal/AiWriterModal';
import { CompactMessengerPanel } from 'components/CompactMessengerPanel/CompactMessengerPanel';
import { MessengerHostInit } from 'components/MessengerHostInit/MessengerHostInit';
import { PartnerChangeRequestModal } from 'components/PartnerChangeRequestModal/PartnerChangeRequestModal';
import PrivateRoute from 'components/PrivateRoute';
import { SideOverlayContainer } from 'components/SideOverlayContainer';
import SmartRouter from 'components/SmartRouter';
import { TemplatesModal } from 'components/TemplatesModal';
import { Container, MainContentColumn, MainWithCompactMessengerRow } from 'components/UI/shared/styled';
import { DATEV_ACCOUNTANT_SYSTEM_ENABLED, HAS_ACCOUNTING_NAME } from 'config/regions/features';
import { AppContextProvider } from 'contexts/AppContextProvider';
import CrmController from 'controllers/Crm';
import LookersController from 'controllers/Lookers';
import PartnerController from 'controllers/PartnerController';
import { getYuzu } from 'core/monitoring/Yuzu/Yuzu.utils';
import { WorkspaceAppProvider } from 'core/workspace/WorkspaceAppProvider';
import { observer } from 'mobx-react-lite';
import {
  DealPage,
  FilterGroupPage,
  LogisticOperatorsPage,
  MessageCenterPage,
  NotFoundPage,
  PrinterPage,
  SignInPage,
  SsoCallbackPage,
  StartPage,
  TemplatesPage,
  TemplatesPlaceholdersPage,
} from 'pages';
import React, { FC, useCallback, useEffect } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { routes } from 'routes';
import { useStore } from 'stores/RootStore';
import { lazyLoadPage } from 'utils/lazyLoad';

import AccountingController from './Accounting';
import BackofficeController from './BackofficeController';
import BankPaymentsController from './BankPayments';
import BillingAccountsController from './BillingAccountsController';
import { DashboardsController } from './DashboardsController';
import DatevController from './Datev';
import LogisticInvoicesController from './LogisticInvoicesController';
import { MasterShippingsController } from './MasterShippingsController';
import RolesController from './RolesController';
import StoragesController from './StoragesController';
import { TeamsController } from './TeamsController';
import UsersController from './UsersController';
import WorkspacesController from './WorkspacesController';

const Header = lazyLoadPage(() =>
  import(/* webpackChunkName: "Header" */ 'components/Header/index').then((r) => r.Header),
);

const BaseControllerComponent: FC = () => {
  const store = useStore();
  const history = useHistory();
  const monitoring = useMonitoringClient();
  const userId = String(store?.userStore?.id || 'Unauthorized');
  const monitoringUserId = store?.userStore?.id ? String(store.userStore.id) : null;

  const yuzuUserId = String(store?.userStore?.id || '');
  const yuzu = getYuzu();

  const yuzuLogContextGetter: Parameters<typeof yuzu.setLogContextGetter>[0] = useCallback(
    ({ baseLogContextGetter }) => {
      return {
        ...baseLogContextGetter(),
        userId: yuzuUserId,
        activeExperiments: {},
      };
    },
    [yuzuUserId],
  );

  useEffect(() => {
    yuzu.setLogContextGetter(yuzuLogContextGetter);
  }, [yuzu, yuzuLogContextGetter]);

  useEffect(() => {
    if (!monitoring) {
      return;
    }

    monitoring.setClientId(userId);
    monitoring.setUserId(monitoringUserId);
  }, [monitoring, monitoringUserId, userId]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    store.history = history;
  }, [store, history]);

  return (
    <AppContextProvider>
      <WorkspaceAppProvider>
        {/* One host: hash/deeplink + messenger event listeners for every route below (incl. standalone MC). */}
        <MessengerHostInit />

        <Switch>
          <Route path={routes.notFound()} component={NotFoundPage} exact />
          <Route path={routes.signInPath()} component={SignInPage} exact />
          <Route path={routes.ssoCallbackPath()} component={SsoCallbackPage} exact />

          <PrivateRoute
            path={routes.messageCenterPath()}
            exact
            component={() => (
              <>
                <MessageCenterPage />
                <TemplatesModal />
                <AiWriterModal />
              </>
            )}
          />

          <PrivateRoute
            path={routes.rootPath()}
            component={() => (
              <>
                {/* General page header */}
                <Header />

                <Container>
                  <MainWithCompactMessengerRow>
                    <MainContentColumn>
                      <SideOverlayContainer />
                      <Route path={routes.rootPath()} component={StartPage} exact />
                      <DashboardsController />
                      <LookersController />
                      <CrmController />
                      <PartnerController />
                      {HAS_ACCOUNTING_NAME && <AccountingController />}
                      {DATEV_ACCOUNTANT_SYSTEM_ENABLED && <DatevController />}
                      <BankPaymentsController />
                      <WorkspacesController />
                      <Route path={routes.printerPath()} component={PrinterPage} />
                      <Route path={routes.smartRouterPath()} component={SmartRouter} />
                      <BillingAccountsController />
                      <StoragesController />
                      <BackofficeController />
                      <UsersController />
                      <RolesController />
                      <LogisticInvoicesController />

                      <Switch>
                        <Route path={routes.logisticOperatorsPath()} component={LogisticOperatorsPage} exact />

                        <Route
                          path={routes.logisticOperatorPath(':logisticOperatorId')}
                          component={LogisticOperatorsPage}
                        />
                      </Switch>

                      <MasterShippingsController />

                      <Switch>
                        <Route path={routes.messageFiltersPath()} component={FilterGroupPage} exact />
                        <Route path={routes.messageFiltersGroupPath(':id')} component={FilterGroupPage} exact />
                      </Switch>

                      <Route path={routes.templatesPlaceholdersPath()} component={TemplatesPlaceholdersPage} />

                      <Route
                        path={[routes.dealsPath(), routes.pDealsPath()]}
                        component={() => (
                          <Route
                            path={[`${routes.dealsPath()}/:dealId`, `${routes.pDealsPath()}/:dealId`]}
                            component={DealPage}
                          />
                        )}
                      />

                      <Route path={routes.templates()} component={TemplatesPage} />
                      <TeamsController />
                      <TemplatesModal />
                      <AiWriterModal />
                      <PartnerChangeRequestModal />
                    </MainContentColumn>

                    <CompactMessengerPanel />
                  </MainWithCompactMessengerRow>
                </Container>
              </>
            )}
          />

          <Redirect to={routes.notFound()} />
        </Switch>
      </WorkspaceAppProvider>
    </AppContextProvider>
  );
};

export const BaseController = observer(BaseControllerComponent);
